import React, { useEffect } from 'react';
import { Sprout, Heart, Calendar, ArrowRight, BookOpen, Share2, Facebook, Twitter, Mail, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import NavBar from '@/components/NavBar';
// Removed Footer import - App.tsx handles global footer
import { BOOKING_URL } from '@/constants/urls';
import { WelcomeModal } from '@/components/WelcomeModal';
import BlogPostingSchema from '@/components/BlogPostingSchema';
import seedCyclingImage from '@/assets/blog/seed-cycling.webp';

const SeedCyclingBlog = () => {
  // Force scroll to top on page load (ignoring anchors)
  useEffect(() => {
    window.scrollTo(0, 0);

    // Update document title and meta tags
    document.title = "Seed Cycling Currimundi | Hormonal Balance Guide";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Learn about seed cycling for hormonal balance at our Currimundi Chinese Medicine clinic. Natural fertility support and menstrual cycle optimization with expert guidance.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Seed Cycling Currimundi | Hormonal Balance Guide");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Learn about seed cycling for hormonal balance at our Currimundi Chinese Medicine clinic. Natural fertility support and menstrual cycle optimization with expert guidance.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/blog/seed-cycling-hormonal-balance");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', seedCyclingImage);
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Seed Cycling Currimundi | Hormonal Balance Guide");
    }

    // Cleanup function
    return () => {
      // Reset title on unmount
      document.title = "Acupuncture Sunshine Coast & Caloundra | Women and The Way - Chinese Medicine";
    };
  }, []);

  return (
    <>
      <BlogPostingSchema
        title="Seed Cycling for Hormonal Balance"
        description="Learn how seed cycling can naturally support your hormonal balance and menstrual health through strategic seed consumption."
        image={seedCyclingImage}
        url="https://womenandtheway.com.au/blog/seed-cycling-hormonal-balance"
        publishedDate="2025-01-12"
        modifiedDate="2025-01-27"
        keywords="seed cycling, hormonal balance, menstrual health, women's health, Sunshine Coast, fertility clinic, Chinese medicine, natural fertility"
        articleSection="Fertility & Women's Health"
      />
      <NavBar />
      <div className="min-h-screen bg-warm-beige pt-24 md:pt-32">
        {/* Hero Section with Image */}
        <section className="relative py-16 px-6 bg-gradient-to-r from-sage-50 via-white to-amber-50">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-center md:justify-between">
              <div className="w-full md:w-1/2 mb-10 md:mb-0">
                <div className="mb-8">
                  <span className="text-5xl md:text-6xl mb-4 block animate-float">🌿</span>
                  <h1 className="font-serif text-4xl md:text-5xl font-bold mb-6 leading-tight bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                    Seed Cycling for Hormonal Balance
                  </h1>
                  <p className="text-xl text-gray-600 font-light">
                    A Simple Step Toward Fertility Wellness
                  </p>
                </div>
              </div>
              <div className="w-full md:w-5/12 relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-200/50">
                  <img 
                    src={seedCyclingImage} 
                    alt="Seed cycling for hormonal balance at Women and the Way, Sunshine Coast – natural seeds and herbs for menstrual health" 
                    className="w-full h-auto object-cover aspect-[4/3]"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg border border-amber-200/50 w-3/4 md:w-1/2">
                  <p className="text-sage-800 font-serif font-medium">
                    "Nourishing your body with nature's wisdom, one seed at a time."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="py-12 px-6">
          <div className="container mx-auto max-w-3xl">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-16">
              <p className="text-lg leading-relaxed text-gray-700 mb-6">
                At Women and the Way, we believe that small, mindful changes in your daily routine can make a big difference - especially when it comes to hormone health and fertility. One gentle, food-based practice we often recommend is seed cycling.
              </p>
              <p className="text-lg leading-relaxed text-gray-700 mb-6">
                Seed cycling is a natural way to support your menstrual cycle by eating specific seeds during different phases. It's simple, affordable, and can be a nourishing part of your preconception care or hormonal wellness journey.
              </p>
              <p className="text-lg leading-relaxed text-gray-700">
                Let's explore how to bring this practice into your daily rhythm - one spoonful at a time.
              </p>
            </div>

            {/* What is Seed Cycling */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🌙</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">What is Seed Cycling?</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Seed cycling involves eating different types of seeds at specific points in your menstrual cycle. Each seed is rich in nutrients that may help regulate the body's production and metabolism of estrogen and progesterone - the key hormones for reproductive health.
                  </p>
                  <h3 className="font-serif text-xl font-semibold mb-4 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Over time, seed cycling may:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Regulate irregular cycles</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Reduce PMS symptoms</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Support ovulation and luteal phase health</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Improve cervical mucus quality</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            {/* Cycle Phases */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🔄</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Step 1: Understand Your Cycle Phases</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    To begin, it helps to understand your menstrual cycle. A typical 28-day cycle has four phases:
                  </p>
                  <div className="space-y-4">
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Menstrual Phase (Days 1–5)</h4>
                      <p className="text-gray-600">Bleeding begins; hormone levels are low.</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Follicular Phase (Days 1–14)</h4>
                      <p className="text-gray-600">Estrogen rises to prepare for ovulation.</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Ovulatory Phase (Around Day 14)</h4>
                      <p className="text-gray-600">Estrogen peaks and ovulation occurs.</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Luteal Phase (Days 15–28)</h4>
                      <p className="text-gray-600">Progesterone increases to support potential pregnancy.</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mt-6">
                    By syncing seed intake to your cycle, you gently support your body's hormonal ebb and flow.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Seed Types */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🌾</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Step 2: Eat These Seeds with the Phases</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="bg-soft-pink border-2 border-amber-200/50 shadow-sm rounded-2xl">
                  <CardContent className="p-8">
                    <h3 className="font-serif text-2xl font-semibold mb-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Follicular Phase (Days 1–14)</h3>
                    <div className="space-y-4 mb-6">
                      <div>
                        <h4 className="font-semibold mb-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Flaxseeds</h4>
                        <p className="text-gray-600 text-sm">Rich in lignans to support estrogen balance</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Pumpkin seeds</h4>
                        <p className="text-gray-600 text-sm">High in zinc to promote progesterone for ovulation prep</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                  <CardContent className="p-8">
                    <h3 className="font-serif text-2xl font-semibold mb-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Luteal Phase (Days 15–28)</h3>
                    <div className="space-y-4 mb-6">
                      <div>
                        <h4 className="font-semibold mb-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Sesame seeds</h4>
                        <p className="text-gray-600 text-sm">Contain lignans and vitamin E to support progesterone</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Sunflower seeds</h4>
                        <p className="text-gray-600 text-sm">Rich in selenium and vitamin E to aid detox and reduce inflammation</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div className="mt-8 p-6 bg-sage-green/10 rounded-lg border-2 border-amber-200/50">
                <p className="text-gray-700">
                  <strong>👉 Use ground seeds for better absorption, and store them in the fridge to preserve nutrients.</strong>
                </p>
              </div>
            </section>

            {/* Make It Easy */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🥣</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Step 3: Make It Easy & Enjoyable</span>
              </h2>
              <Card className="bg-white rounded-2xl shadow-sm border-2 border-amber-200/50 mb-8">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    No need to overhaul your entire diet - just sprinkle these into your current routine!
                  </p>
                  <h3 className="font-serif text-xl font-semibold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Ideas to try:</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Add ground flax or pumpkin to smoothies, oats, or soups</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Mix sunflower and sesame into bliss balls, dressings, or yogurt</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Create two-week mixes in a jar for easy daily scooping</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            {/* Be Consistent */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">📅</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Step 4: Be Consistent</span>
              </h2>
              <Card className="bg-white rounded-2xl shadow-sm border-2 border-amber-200/50 mb-8">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700">
                    Hormone balance takes time. Think of seed cycling as a gentle, long-term ally rather than a quick fix. Stick with it for at least 3 months to start noticing changes.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Track Your Journey */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">✍️</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Step 5: Track Your Journey</span>
              </h2>
              <Card className="bg-white rounded-2xl shadow-sm border-2 border-amber-200/50 mb-8">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Noticing what shifts can be empowering:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">💚</span>
                      <span className="text-gray-700">Mood changes</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-lg mr-3">💚</span>
                      <span className="text-gray-700">PMS reduction</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-lg mr-3">💚</span>
                      <span className="text-gray-700">Better sleep</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-lg mr-3">💚</span>
                      <span className="text-gray-700">Clearer skin</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-lg mr-3">💚</span>
                      <span className="text-gray-700">Changes in cervical mucus or cycle length</span>
                    </div>
                  </div>
                  <p className="text-gray-600 italic">Use a journal or app to track and reflect on your progress.</p>
                </CardContent>
              </Card>
            </section>

            {/* No Period Section */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🌕</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Step 6: No Period? No Problem.</span>
              </h2>
              <div className="bg-soft-pink rounded-lg p-8 border-2 border-amber-200/50">
                <p className="text-gray-700 text-center mb-6">
                  If your cycle isn't regular - or if you don't bleed - you can sync your seed cycling with the moon phases:
                </p>
                <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto text-center">
                  <div>
                    <p className="font-medium text-sage-800">New Moon → Full Moon</p>
                    <p className="text-gray-600">Flax & Pumpkin</p>
                  </div>
                  <div>
                    <p className="font-medium text-sage-800">Full Moon → New Moon</p>
                    <p className="text-gray-600">Sesame & Sunflower</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Work With Practitioner */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🤝</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Step 7: Work With a Practitioner</span>
              </h2>
              <Card className="bg-white rounded-2xl shadow-sm border-2 border-amber-200/50 mb-8">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Seed cycling is a beautiful starting point - but if you're dealing with PCOS, endometriosis, or fertility challenges, personalized care is key.
                  </p>
                  <h3 className="font-serif text-xl font-semibold mb-4 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Our Chinese medicine practitioners at Women and the Way can help you:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Decode your body's signals</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Support ovulation and hormone health naturally</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Combine seed cycling with acupuncture and herbs for a deeper effect</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            {/* Summary */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">💛</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">In Summary</span>
              </h2>
              <Card className="bg-white rounded-2xl shadow-sm border-2 border-amber-200/50 mb-8">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700">
                    Seed cycling is a gentle, empowering practice that aligns you with your natural rhythms. When you're trying to conceive or simply seeking balance, it's these small, consistent acts of self-care that create lasting change.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Share Component Placeholder */}
            <div className="my-8 text-center">
              {/* TODO: Add social share buttons here */}
            </div>

            {/* Call to Action */}
            <section className="text-center py-16">
              <div className="bg-sage-800 rounded-2xl p-12 shadow-lg border-2 border-amber-200/50 max-w-2xl mx-auto">
                <h2 className="font-serif text-3xl font-bold text-white mb-6">🌼 Book your consult with one of our women's health practitioners</h2>
                <p className="text-lg text-sage-100 mb-8 max-w-2xl mx-auto">
                  Explore how you can support your cycle naturally - from seed to soul.
                </p>
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-semibold rounded-full" onClick={() => window.open(BOOKING_URL, '_blank')}>
                  Book Your Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-sm text-sage-200 mt-4">Nourish your cycle, nourish your life</p>
              </div>
            </section>
          </div>
        </main>
      </div>
              {/* Footer removed - App.tsx handles global footer */}
    </>
  );
};

export default SeedCyclingBlog; 